import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { Shield, Clock, Truck, FileText, Settings, Factory } from 'lucide-react';
import RelatedCategories from '../../components/catalog/RelatedCategories';
import ContactForm from '../../components/forms/ContactForm';

const services = [
  {
    icon: <FileText className="w-12 h-12 text-primary-600" />,
    title: 'Проектирование',
    description: 'Разработка проектной документации для электроснабжения объектов',
    features: [
      'Расчет нагрузок',
      'Выбор оборудования',
      'Схемы подключения',
      'Сметная документация',
    ],
  },
  {
    icon: <Factory className="w-12 h-12 text-primary-600" />,
    title: 'Производство',
    description: 'Изготовление оборудования по техническому заданию',
    features: ['Трансформаторы', 'КТПН', 'Щитовое оборудование', 'КСО'],
  },
  {
    icon: <Truck className="w-12 h-12 text-primary-600" />,
    title: 'Поставка',
    description: 'Комплексные поставки оборудования и материалов',
    features: ['Силовые кабели', 'Электроустановочные изделия', 'Системы освещения', 'Автоматика'],
  },
  {
    icon: <Settings className="w-12 h-12 text-primary-600" />,
    title: 'Монтаж',
    description: 'Монтаж и пусконаладка оборудования',
    features: [
      'Установка трансформаторов',
      'Монтаж КТПН',
      'Прокладка кабеля',
      'Пусконаладочные работы',
    ],
  },
];

const TurnkeyPage: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Комплектация под ключ - комплексные решения | Проминнова</title>
        <meta
          name="description"
          content="Комплексные решения для энергоснабжения объектов. Полный комплект оборудования и материалов. Проектирование, поставка, монтаж."
        />
      </Helmet>

      <div className="bg-primary-50 py-12">
        <div className="container-custom">
          <Breadcrumbs />
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Комплектация под ключ</h1>
          <p className="text-xl text-gray-600 max-w-3xl">
            Комплексные решения для энергоснабжения: от проектирования до монтажа
          </p>
        </div>
      </div>

      <div className="container-custom py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          <div>
            <h2 className="text-2xl font-bold mb-6">О комплексных решениях</h2>
            <p className="text-lg text-gray-600 mb-8">
              Мы предлагаем полный спектр услуг по реализации проектов энергоснабжения любой
              сложности: от разработки проектной документации до пусконаладочных работ.
            </p>
            <div className="space-y-6">
              <div className="flex items-start">
                <Shield className="w-5 h-5 text-primary-600 mt-1 mr-3" />
                <div>
                  <h3 className="font-medium mb-1">Комплексный подход</h3>
                  <p className="text-gray-600">Все работы выполняются одним подрядчиком</p>
                </div>
              </div>
              <div className="flex items-start">
                <Clock className="w-5 h-5 text-primary-600 mt-1 mr-3" />
                <div>
                  <h3 className="font-medium mb-1">Оптимальные сроки</h3>
                  <p className="text-gray-600">Реализация проекта от 30 дней</p>
                </div>
              </div>
              <div className="flex items-start">
                <Truck className="w-5 h-5 text-primary-600 mt-1 mr-3" />
                <div>
                  <h3 className="font-medium mb-1">Работаем по всей России</h3>
                  <p className="text-gray-600">Выполняем проекты в любом регионе</p>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-xl font-semibold mb-6">Обсудить проект</h3>
            <ContactForm />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {services.map((service, index) => (
            <div
              key={index}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
            >
              <div className="p-6">
                <div className="mb-6">{service.icon}</div>
                <h3 className="text-xl font-semibold mb-2">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-600">
                      <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-2"></div>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        <RelatedCategories currentCategory="turnkey" />

        <div className="prose prose-lg max-w-none">
          <h2>Комплексные решения для энергоснабжения</h2>
          <p>
            Компания «Проминнова» предлагает комплексные решения по организации энергоснабжения
            промышленных предприятий, объектов инфраструктуры и гражданского строительства. Мы
            обеспечиваем полный цикл работ: от проектирования до пусконаладки.
          </p>

          <h3>Преимущества работы с нами:</h3>
          <ul>
            <li>Комплексный подход к реализации проектов</li>
            <li>Собственное производство оборудования</li>
            <li>Прямые поставки от производителей</li>
            <li>Квалифицированный персонал</li>
            <li>Гарантийное обслуживание</li>
            <li>Техническая поддержка</li>
          </ul>

          <p>
            Мы берем на себя все этапы реализации проекта и обеспечиваем его успешное выполнение в
            установленные сроки. Наш опыт и профессионализм позволяют решать задачи любой сложности.
          </p>
        </div>
      </div>
    </>
  );
};

export default TurnkeyPage;
